import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  archiveTemplate,
  cloneTemplate,
  createProject,
  createTemplate,
  deleteTemplate,
  forkTemplate,
  listCalendars,
  listTemplates,
  patchTemplate,
  promoteTemplate,
  simulateCreateProjectFromTemplate,
  validateTemplate,
  type ApprovalRecord,
  type AuditEntry,
  type Calendar,
  type CreateProjectFromTemplateRequest,
  type Template,
  type TemplateDraft,
  type ValidationReport,
} from "@/api/templates";

export const TEMPLATES_KEY = ["templates"] as const;
export const CALENDARS_KEY = ["calendars"] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function useTemplates() {
  return useQuery<Template[], Error>({
    queryKey: TEMPLATES_KEY,
    queryFn: listTemplates,
    retry: noRetryOn401,
    staleTime: 60_000,
  });
}

export function useCalendars() {
  return useQuery<Calendar[], Error>({
    queryKey: CALENDARS_KEY,
    queryFn: listCalendars,
    retry: noRetryOn401,
    staleTime: 5 * 60_000,
  });
}

export function useSimulateCreateProjectFromTemplate() {
  const qc = useQueryClient();
  return useMutation<ApprovalRecord, Error, CreateProjectFromTemplateRequest>({
    mutationFn: simulateCreateProjectFromTemplate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approvals", "pending"] });
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation<AuditEntry, Error, CreateProjectFromTemplateRequest>({
    mutationFn: createProject,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

function invalidateTemplates(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
}

export function useValidateTemplate() {
  return useMutation<ValidationReport, Error, TemplateDraft>({
    mutationFn: validateTemplate,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation<Template, Error, TemplateDraft>({
    mutationFn: createTemplate,
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function usePatchTemplate() {
  const qc = useQueryClient();
  return useMutation<Template, Error, { pk: number; draft: TemplateDraft }>({
    mutationFn: ({ pk, draft }) => patchTemplate(pk, draft),
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteTemplate,
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function useForkTemplate() {
  const qc = useQueryClient();
  return useMutation<Template, Error, number>({
    mutationFn: forkTemplate,
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function usePromoteTemplate() {
  const qc = useQueryClient();
  return useMutation<Template, Error, number>({
    mutationFn: promoteTemplate,
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function useArchiveTemplate() {
  const qc = useQueryClient();
  return useMutation<Template, Error, number>({
    mutationFn: archiveTemplate,
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function useCloneTemplate() {
  const qc = useQueryClient();
  return useMutation<Template, Error, { pk: number; template_id: string; name: string }>({
    mutationFn: ({ pk, template_id, name }) => cloneTemplate(pk, { template_id, name }),
    onSuccess: () => invalidateTemplates(qc),
  });
}
